import React, { useEffect, useState } from "react";
import "./StatementHistory.css";
import { useSelector } from "react-redux";
import { GetInvoiceHistoryService } from "../../../Services/InvoiceService/InvoiceService";
import { Spin, Empty } from "antd";
import { InvoiceHistoryEntry } from "../../../Pages/Steps/3Inbox/InvoiceFormHistory/Invoiceformhistory.types";

type HistoryEntry = InvoiceHistoryEntry;

const formatTimestamp = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString([], {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTitle = (entry: HistoryEntry): string => {
  switch (entry.actionType) {
    case "SUBMITTED":       return "Added to Inbox";
    case "PUBLISHED_MANUAL":
    case "PUBLISHED_AUTO":  return "Published";
    case "ARCHIVED":        return "Archived";
    case "RECONCILED":      return "Marked as Reconciled";
    case "UNARCHIVED":      return "Unarchived";
    case "FIELD_EDITED":    return "Field Edited";
    case "VIEWED":          return "Viewed";
    case "PAGE_SPLIT":      return "Document Split";
    case "MERGED":          return "Merged";
    case "UNMERGED":        return "Unmerged";
    default:                return entry.actionType;
  }
};

const getSubtitle = (entry: HistoryEntry): string => {
  const who = entry.performedBy || "System";
  const time = formatTimestamp(entry.performedAt);

  if (entry.actionType === "SUBMITTED" && entry.method) {
    return `${entry.method} by ${who} — ${time}`;
  }
  if (entry.actionType === "FIELD_EDITED" && entry.fieldChange) {
    return `${entry.fieldChange.fieldName}: "${entry.fieldChange.oldValue ?? "—"}" → "${entry.fieldChange.newValue ?? "—"}" — ${who} — ${time}`;
  }
  return `${who} — ${time}`;
};

interface StatementHistoryProps {
  itemId?: number;
  entityType?: "Invoice" | "SupplierStatement";
}

const StatementHistory: React.FC<StatementHistoryProps> = ({
  itemId: propItemId,
  entityType = "Invoice",
}) => {
  const selectedInvoiceData = useSelector((state: any) => state.selectedInvoiceData.selectedInvoiceData);
  const itemId = propItemId ?? selectedInvoiceData?.invoiceId;

  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    setLoading(true);

    GetInvoiceHistoryService(itemId, entityType).then((res: HistoryEntry[]) => {
      if (cancelled) return;
      const sorted = [...res].sort(
        (a, b) => new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime()
      );
      setEntries(sorted);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [entityType, itemId]);

  if (loading) {
    return (
      <div className="sh-centered"><Spin size="small" /></div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="sh-centered"><Empty description="No history available." /></div>
    );
  }

  return (
    <div className="sh-container">
      {entries.map((entry) => (
        <div key={entry.id} className="sh-item">
          <div className="sh-title">{getTitle(entry)}</div>
          <div className="sh-subtitle">{getSubtitle(entry)}</div>
        </div>
      ))}
    </div>
  );
};

export default StatementHistory;
